import { useEffect, useState } from 'react';
import { Bookmark, ChevronDown, Check, Trash2, Save, Users } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import { OperationsViewState, SavedView } from './savedViews';

interface SavedViewPickerProps {
  views: SavedView[];
  activeViewId: string | null;
  currentState: OperationsViewState;
  currentUserId?: string;
  dirty: boolean;
  onSelect: (id: string) => void;
  onCreate: (name: string, isShared: boolean) => void;
  onUpdate: (id: string) => void;
  onDelete: (id: string) => void;
  onClear: () => void;
}

export function SavedViewPicker({
  views,
  activeViewId,
  currentUserId,
  dirty,
  onSelect,
  onCreate,
  onUpdate,
  onDelete,
  onClear,
}: SavedViewPickerProps) {
  const [open, setOpen] = useState(false);
  const [saveMenuOpen, setSaveMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newShared, setNewShared] = useState(false);

  const activeView = views.find((v) => v.id === activeViewId) ?? null;
  const displayName = activeView ? activeView.name : 'Default View';
  const isOwner = activeView ? activeView.ownerId === currentUserId : true;

  const myViews = views.filter((v) => v.ownerId === currentUserId);
  const sharedViews = views
    .filter((v) => v.ownerId !== currentUserId && v.isShared)
    .sort((a, b) => a.ownerEmail.localeCompare(b.ownerEmail) || a.name.localeCompare(b.name));

  useEffect(() => {
    if (saveDialogOpen) {
      setNewName(activeView ? `${activeView.name} copy` : '');
      setNewShared(false);
    }
  }, [saveDialogOpen, activeView]);

  const handleCreate = () => {
    const name = newName.trim();
    if (!name) return;
    onCreate(name, newShared);
    setNewName('');
    setNewShared(false);
    setSaveDialogOpen(false);
  };

  return (
    <div className="flex items-center gap-1 rounded-md border border-slate-200 bg-white pr-1 h-9 w-[320px] shrink-0">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button className="flex items-center gap-2 pl-3 pr-2 h-full text-sm rounded-l-md hover:bg-slate-50 flex-1 min-w-0">
            <span className="text-xs uppercase tracking-wider text-slate-400 font-semibold shrink-0">
              View:
            </span>
            <span className="flex items-center gap-1.5 flex-1 min-w-0">
              <span
                className={cn(
                  'text-xs italic text-slate-400 shrink-0',
                  !dirty && 'invisible'
                )}
              >
                (modified)
              </span>
              <span className="font-semibold text-slate-900 truncate">{displayName}</span>
            </span>
            <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-80 p-1">
          <div className="py-1 max-h-80 overflow-y-auto">
            <button
              className={cn(
                'w-full flex items-center gap-2 px-3 py-2 text-sm rounded-sm hover:bg-slate-50 text-left',
                !activeViewId && 'bg-slate-50 font-medium'
              )}
              onClick={() => {
                onClear();
                setOpen(false);
              }}
            >
              {!activeViewId ? (
                <Check className="w-4 h-4 text-slate-900" />
              ) : (
                <span className="w-4 h-4" />
              )}
              <span className="text-slate-900">Default View</span>
            </button>

            {myViews.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-0.5 text-[11px] uppercase tracking-wider font-medium text-slate-400">
                  My Views
                </div>
                {myViews.map((v) => (
                  <ViewRow
                    key={v.id}
                    view={v}
                    active={activeViewId === v.id}
                    canDelete
                    onSelect={() => {
                      onSelect(v.id);
                      setOpen(false);
                    }}
                    onDelete={() => onDelete(v.id)}
                  />
                ))}
              </>
            )}

            {sharedViews.length > 0 && (
              <>
                <div className="px-3 pt-2 pb-0.5 text-[11px] uppercase tracking-wider font-medium text-slate-400 flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  Shared
                </div>
                {sharedViews.map((v) => (
                  <ViewRow
                    key={v.id}
                    view={v}
                    active={activeViewId === v.id}
                    canDelete={false}
                    showOwner
                    onSelect={() => {
                      onSelect(v.id);
                      setOpen(false);
                    }}
                    onDelete={() => {}}
                  />
                ))}
              </>
            )}

            {views.length === 0 && (
              <div className="px-3 py-6 text-xs text-slate-400 text-center">
                No saved views yet
              </div>
            )}
          </div>
        </PopoverContent>
      </Popover>

      <Popover
        open={saveMenuOpen}
        onOpenChange={(o) => dirty && setSaveMenuOpen(o)}
      >
        <PopoverTrigger asChild>
          <button
            disabled={!dirty}
            className={cn(
              'flex items-center justify-center w-7 h-7 rounded-md shrink-0 transition-opacity',
              dirty
                ? 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'
                : 'opacity-0 pointer-events-none'
            )}
            title="Save view"
          >
            <Save className="w-4 h-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-56 p-1">
          {activeView && isOwner && (
            <button
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 rounded-sm text-left"
              onClick={() => {
                onUpdate(activeView.id);
                setSaveMenuOpen(false);
              }}
            >
              <Save className="w-4 h-4 text-slate-500" />
              Save changes
            </button>
          )}
          <button
            className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-slate-900 hover:bg-slate-50 rounded-sm text-left"
            onClick={() => {
              setSaveMenuOpen(false);
              setSaveDialogOpen(true);
            }}
          >
            <Save className="w-4 h-4 text-slate-500" />
            Save as new view…
          </button>
        </PopoverContent>
      </Popover>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-900 mb-1.5">
                View name
              </label>
              <Input
                autoFocus
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleCreate();
                }}
                placeholder="My view"
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Checkbox
                checked={newShared}
                onCheckedChange={(v) => setNewShared(!!v)}
              />
              <span className="flex items-center gap-1.5 text-sm text-slate-900">
                <Users className="w-3.5 h-3.5 text-slate-500" />
                Share with everyone
              </span>
            </label>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setSaveDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleCreate}
                disabled={!newName.trim()}
                className="bg-slate-900 hover:bg-slate-800 text-white"
              >
                Save
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ViewRowProps {
  view: SavedView;
  active: boolean;
  canDelete: boolean;
  showOwner?: boolean;
  onSelect: () => void;
  onDelete: () => void;
}

function ViewRow({ view, active, canDelete, showOwner, onSelect, onDelete }: ViewRowProps) {
  return (
    <div
      className={cn(
        'group flex items-center gap-2 px-3 py-1.5 text-sm rounded-sm hover:bg-slate-50',
        active && 'bg-slate-50 font-medium'
      )}
    >
      <button className="flex items-center gap-2 flex-1 text-left min-w-0" onClick={onSelect}>
        {active ? (
          <Check className="w-4 h-4 text-slate-900 shrink-0" />
        ) : (
          <span className="w-4 h-4 shrink-0" />
        )}
        <Bookmark className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="flex flex-col min-w-0 leading-tight">
          <span className="text-slate-900 truncate">{view.name}</span>
          {showOwner && (
            <span className="text-[11px] italic text-slate-400 truncate">
              {view.ownerEmail || 'unknown'}
            </span>
          )}
        </span>
      </button>
      {canDelete && (
        <button
          className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500"
          onClick={onDelete}
          title="Delete view"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
